import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Modal,
  Descriptions,
  Table,
  Tag,
  Segmented,
  Empty,
  Spin,
  Typography,
  Tabs,
  Button,
  Space,
  Select,
  Input,
  DatePicker,
  Badge,
  message,
  Tooltip,
} from "antd";
import { CopyOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import moment from "moment";
import {
  flattenPayload,
  formatPayloadValue,
  formatIst,
  getStatusColor,
  getStatusLabel,
  getSourceLabel,
  getUserDisplayName,
  getPreLeadDisplayName,
  getPayloadPhone,
  safeStringify,
} from "../Utils/preLeadPayload";
import {
  assignPreLead,
  transferPreLead,
  setPreLeadFollowUp,
  dismissPreLead,
  reopenPreLead,
  positivePreLead,
} from "../Actions/preLeadsAction";
import PreLeadNotes from "./PreLeadNotes";
import PreLeadClientModal from "./PreLeadClientModal";

const { Text } = Typography;
const { TextArea } = Input;

/**
 * Renders `payload` generically - a flat key/value table built from whatever
 * arrived, plus the raw JSON. Nothing here assumes a particular key exists, so
 * a payload with an unexpected shape still renders instead of blanking out.
 */
const PayloadView = ({ payload }) => {
  const [mode, setMode] = useState("table");

  const rows = flattenPayload(payload).map((row, index) => ({
    key: `${row.path}-${index}`,
    ...row,
  }));

  if (rows.length === 0) {
    return <Empty description="This pre-lead arrived with an empty payload" />;
  }

  return (
    <>
      <Segmented
        size="small"
        value={mode}
        onChange={setMode}
        options={[
          { label: "Fields", value: "table" },
          { label: "Raw JSON", value: "json" },
        ]}
        style={{ marginBottom: 12 }}
      />

      {mode === "table" ? (
        <Table
          size="small"
          pagination={false}
          dataSource={rows}
          scroll={{ x: "max-content" }}
          columns={[
            {
              title: "Field",
              dataIndex: "path",
              key: "path",
              width: "40%",
              render: (path) => <Text code>{path}</Text>,
            },
            {
              title: "Value",
              dataIndex: "value",
              key: "value",
              render: (value) => (
                <span style={{ wordBreak: "break-word" }}>
                  {formatPayloadValue(value)}
                </span>
              ),
            },
          ]}
        />
      ) : (
        <pre
          style={{
            background: "#f5f5f5",
            padding: 12,
            borderRadius: 4,
            maxHeight: "50vh",
            overflow: "auto",
            margin: 0,
          }}
        >
          {safeStringify(payload)}
        </pre>
      )}
    </>
  );
};

/** Picks a target user for assign/transfer. Employees only - admins don't work leads. */
const UserPicker = ({ value, onChange, excludeUserId }) => {
  const users = useSelector((state) => state.users.users);

  const options = (users || [])
    .filter((user) => (user.role === "Employee" || user.role === "Admin") && user.id !== excludeUserId)
    .map((user) => ({
      value: user.id,
      label: getUserDisplayName(user),
    }));

  return (
    <Select
      showSearch
      optionFilterProp="label"
      placeholder="Select an employee"
      style={{ width: "100%" }}
      value={value}
      onChange={onChange}
      options={options}
      notFoundContent="No other employees found"
    />
  );
};

const ViewPreLead = ({
  open,
  onClose,
  preLead,
  loading,
  currentUser,
  onChanged,
}) => {
  const dispatch = useDispatch();
  const {
    isAssignPreLeadLoading,
    isTransferPreLeadLoading,
    isFollowUpPreLeadLoading,
    isDismissPreLeadLoading,
    isReopenPreLeadLoading,
    isPositivePreLeadLoading,
  } = useSelector((state) => state.preLeads);

  const [handoffOpen, setHandoffOpen] = useState(false);
  const [handoffUserId, setHandoffUserId] = useState(undefined);
  const [dismissOpen, setDismissOpen] = useState(false);
  const [dismissReason, setDismissReason] = useState("");
  const [clientOpen, setClientOpen] = useState(false);

  const isAdmin = currentUser?.role === "Admin";
  const isAssignee = preLead?.assigned_to?.id === currentUser?.id;
  // Mirrors the backend policy: admin or the current holder may work the lead.
  const canWork = isAdmin || isAssignee;
  const isDismissed = preLead?.status === "dismissed";
  // Assign is the admin's route, transfer is the holder's. Same effect server
  // side, separate routes so the permission boundary stays explicit.
  const useAssignRoute = isAdmin && !isAssignee;

  const afterChange = () => {
    onChanged && onChanged();
  };

  const handleHandoff = () => {
    if (!handoffUserId) {
      message.error("Select an employee first");
      return;
    }
    const action = useAssignRoute ? assignPreLead : transferPreLead;
    dispatch(
      action(
        preLead.id,
        handoffUserId,
        () => {
          message.success(
            useAssignRoute ? "Pre-lead assigned" : "Pre-lead transferred",
          );
          setHandoffOpen(false);
          setHandoffUserId(undefined);
          afterChange();
        },
        (error) => message.error(error || "Could not hand this pre-lead over"),
      ),
    );
  };

  const handleFollowUp = (picked) => {
    // A bare YYYY-MM-DD is read as midnight IST server-side, which sidesteps
    // any browser-zone conversion. Follow-ups are a day-level concept anyway -
    // the overdue/today/upcoming buckets are computed by day, not timestamp.
    const value = picked ? picked.format("YYYY-MM-DD") : null;
    dispatch(
      setPreLeadFollowUp(
        preLead.id,
        value,
        () => {
          message.success(value ? "Follow-up set" : "Follow-up cleared");
          afterChange();
        },
        (error) => message.error(error || "Could not set the follow-up date"),
      ),
    );
  };

  const handleDismiss = () => {
    dispatch(
      dismissPreLead(
        preLead.id,
        dismissReason.trim() || null,
        () => {
          message.success("Pre-lead dismissed");
          setDismissOpen(false);
          setDismissReason("");
          afterChange();
        },
        (error) => message.error(error || "Could not dismiss this pre-lead"),
      ),
    );
  };

  const handleReopen = () => {
    dispatch(
      reopenPreLead(
        preLead.id,
        () => {
          message.success("Pre-lead reopened");
          afterChange();
        },
        (error) => message.error(error || "Could not reopen this pre-lead"),
      ),
    );
  };

  const handlePositive = () => {
    dispatch(
      positivePreLead(
        preLead.id,
        () => {
          message.success("Pre-lead marked as positive");
          afterChange();
        },
        (error) => message.error(error || "Could not mark this pre-lead as positive"),
      ),
    );
  };

  // The stored value is UTC; the picker should show the IST calendar day, so
  // the date is reduced to an IST day string before dayjs sees it.
  const followUpValue = preLead?.follow_up_at
    ? dayjs(moment(preLead.follow_up_at).utcOffset(330).format("YYYY-MM-DD"))
    : null;

  const busy =
    isAssignPreLeadLoading ||
    isTransferPreLeadLoading ||
    isFollowUpPreLeadLoading ||
    isDismissPreLeadLoading ||
    isReopenPreLeadLoading ||
    isPositivePreLeadLoading;

  return (
    <Modal
      title={preLead ? `Cloud Lead #${preLead.id}` : "Cloud Lead"}
      open={open}
      onCancel={onClose}
      footer={null}
      width={860}
      bodyStyle={{ maxHeight: "80vh", overflowY: "auto" }}
      destroyOnClose
    >
      <Spin spinning={!!loading || busy}>
        {preLead ? (
          <>
            {canWork && (
              <Space wrap style={{ marginBottom: 16 }}>
                <Button onClick={() => setHandoffOpen(true)}>
                  {useAssignRoute ? "Assign" : "Transfer"}
                </Button>
                <Button onClick={() => setClientOpen(true)}>
                  {preLead.client_id ? "View client match" : "Create client"}
                </Button>
                {isDismissed ? (
                  <Button onClick={handleReopen}>Reopen</Button>
                ) : (
                  <Button danger onClick={() => setDismissOpen(true)}>
                    Dismiss
                  </Button>
                )}
                <Button onClick={handlePositive}>Positive</Button>
                <Space size={4}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Follow-up
                  </Text>
                  <DatePicker
                    value={followUpValue}
                    onChange={handleFollowUp}
                    format="DD/MM/YYYY"
                    placeholder="Not set"
                    disabled={isDismissed}
                  />
                </Space>
              </Space>
            )}

            <Descriptions
              size="small"
              column={2}
              bordered
              style={{ marginBottom: 16 }}
            >
              <Descriptions.Item label="Source">
                {getSourceLabel(preLead.source)}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={getStatusColor(preLead.status)}>
                  {getStatusLabel(preLead.status)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Assigned to">
                {preLead.assigned_to ? (
                  getUserDisplayName(preLead.assigned_to)
                ) : (
                  <Text type="secondary">Unassigned</Text>
                )}
              </Descriptions.Item>

              <Descriptions.Item label="Assigned by">
                {preLead.assigned_by
                  ? getUserDisplayName(preLead.assigned_by)
                  : "-"}
              </Descriptions.Item>

              <Descriptions.Item label="Name">
                {getPreLeadDisplayName(preLead) || "-"}
              </Descriptions.Item>

              <Descriptions.Item label="Phone">
                {(() => {
                  const phone = formatPayloadValue(
                    getPayloadPhone(preLead.payload),
                  );
                  return phone && phone !== "-" ? (
                    <Space size={4}>
                      <span>{phone}</span>
                      <Tooltip title="Copy mobile number">
                        <CopyOutlined
                          style={{ cursor: "pointer", color: "#1890ff" }}
                          onClick={() => {
                            navigator.clipboard.writeText(phone);
                            message.success("Mobile number copied!");
                          }}
                        />
                      </Tooltip>
                    </Space>
                  ) : (
                    "-"
                  );
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="Received">
                {formatIst(preLead.received_at)}
              </Descriptions.Item>
              <Descriptions.Item label="Follow-up">
                {preLead.follow_up_at ? formatIst(preLead.follow_up_at) : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Portal Lead ID">
                {preLead.external_id || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Client">
                {preLead.client ? (
                  `${preLead.client.name || `#${preLead.client.id}`}${
                    preLead.client.mobile_number
                      ? ` · ${preLead.client.mobile_number}`
                      : ""
                  }`
                ) : (
                  <Text type="secondary">Not linked</Text>
                )}
              </Descriptions.Item>
              {isDismissed && (
                <Descriptions.Item label="Dismissed" span={2}>
                  {formatIst(preLead.dismissed_at)}
                  {preLead.dismissed_by
                    ? ` by ${getUserDisplayName(preLead.dismissed_by)}`
                    : ""}
                  {preLead.dismissal_reason
                    ? ` — ${preLead.dismissal_reason}`
                    : ""}
                </Descriptions.Item>
              )}
            </Descriptions>

            <Tabs
              defaultActiveKey="payload"
              items={[
                {
                  key: "payload",
                  label: "Payload",
                  children: <PayloadView payload={preLead.payload} />,
                },
                {
                  key: "activity",
                  label: (
                    <Badge
                      count={preLead.notes_count || 0}
                      size="small"
                      offset={[8, -2]}
                    >
                      Activity
                    </Badge>
                  ),
                  children: (
                    <PreLeadNotes
                      preLeadId={preLead.id}
                      currentUser={currentUser}
                      canWrite={canWork}
                      onChanged={afterChange}
                    />
                  ),
                },
              ]}
            />

            <Modal
              title={useAssignRoute ? "Assign pre-lead" : "Transfer pre-lead"}
              open={handoffOpen}
              onCancel={() => setHandoffOpen(false)}
              onOk={handleHandoff}
              okText={useAssignRoute ? "Assign" : "Transfer"}
              destroyOnClose
            >
              <UserPicker
                value={handoffUserId}
                onChange={setHandoffUserId}
                excludeUserId={preLead.assigned_to?.id}
              />
            </Modal>

            <Modal
              title="Dismiss this pre-lead?"
              open={dismissOpen}
              onCancel={() => setDismissOpen(false)}
              onOk={handleDismiss}
              okText="Dismiss"
              okButtonProps={{ danger: true }}
              destroyOnClose
            >
              <p>
                It stays visible under the Dismissed filter and can be reopened.
                The reason is the only structured record of why this lead died.
              </p>
              <TextArea
                rows={3}
                value={dismissReason}
                onChange={(e) => setDismissReason(e.target.value)}
                placeholder="e.g. Not interested — already bought"
              />
            </Modal>

            <PreLeadClientModal
              open={clientOpen}
              onClose={() => setClientOpen(false)}
              preLead={preLead}
              onResolved={afterChange}
            />
          </>
        ) : (
          !loading && <Empty description="No pre-lead selected" />
        )}
      </Spin>
    </Modal>
  );
};

export default ViewPreLead;
